import { useDisclosure } from "@mantine/hooks";
import { ContextModalProps } from "@mantine/modals";
import { useRouter } from "@tanstack/react-router";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import { createContext, memo, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { z } from "zod";

import { Input } from "@/components/@custom/Input";
import { UpdateTons } from "@/components/ModalVehiclesAttendance";
import { useRefetch } from "@/contexts";
import { useSession } from "@/hooks/@custom/useSession";
import api from "@/services/api";
import { Role } from "@/store";
import { handleMaskQtyTransported } from "@/utils/mask-qty-transported";

import { ExpeditionsQueueStatusModalProps } from ".";
import { ExpeditionActionsBase } from "./base/ExpeditionActionsBase";
import { ExpeditionQueueListItemBase } from "./ExpeditionQueueListItemBase";
import { ExpeditionQueueStatusBase } from "./ExpeditionQueueStatusBase";
import { useVehiclesStatus } from "./useVehiclesStatus";

type TonsOptions = {
  onInit?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
  onFinally?: () => void;
};
type TonsContextProps = {
  handleUpdateTons: (params: UpdateTons, options?: TonsOptions) => Promise<void>;
};
const TonsContext = createContext({} as TonsContextProps);
export const useTons = () => useContext(TonsContext);

interface VehiclesAttendedData {
  id: number;
  attended_at: string;
  driver: {
    id: number;
    name: string;
  };
  scheduling: {
    id: number;
    qtyTransported: number;
  };
  truck: {
    licensePlate: string;
  };
}

type ExpeditionsQueueStatusAttendedModalProps = ExpeditionsQueueStatusModalProps & {};

export function ExpeditionsQueueStatusAttendedModal(_props: ContextModalProps<ExpeditionsQueueStatusAttendedModalProps>) {
  const { data, search, activeArray, state, actions } = useVehiclesStatus<VehiclesAttendedData>({
    type: "attended"
  });

  const vehiclesData = useVehiclesAttendance();
  const tonesManagement = useTonesManagement();

  const isFirstRender = useRef(true);

  const handleUpdateTons = async ({ schedulingId, newQtyTransported, driverId }: UpdateTons, options?: TonsOptions) => {
    const qtyTransportedSchema = z
      .string()
      .nonempty("A quantidade transportada não pode estar vazia")
      .refine((val) => {
        const onlyNumbersWithCommasOptional = /^[0-9]+(,[0-9]+)?$/.test(val);

        const numberValue = Number(val.replace(",", "."));
        return !isNaN(numberValue) && numberValue > 0 && onlyNumbersWithCommasOptional;
      });
    const parsedQtyTransported = qtyTransportedSchema.safeParse(newQtyTransported);

    if (!parsedQtyTransported.success) {
      toast.error(parsedQtyTransported.error.errors[0].message);
      return;
    }

    tonesManagement.setIsUpdateNewTon(true);

    const formatQtyTransported = newQtyTransported.replace(/,/g, ".");
    options?.onInit?.();
    try {
      //TODO - Erro ao editar peso
      const { data } = await api.put(`/scheduling/update`, {
        id: schedulingId,
        qtyTransported: Number(formatQtyTransported)
      });
      options?.onSuccess?.();
      actions.refetch();

      const updateVehiclesAttended = vehiclesData.vehiclesAttended.data.map((vehicle) => {
        return vehicle.id === driverId
          ? {
              ...vehicle,
              scheduling: {
                id: vehicle.scheduling.id,
                qtyTransported: data.qtyTransported
              }
            }
          : vehicle;
      });

      vehiclesData.setVehiclesAttended((prev) => ({ ...prev, data: updateVehiclesAttended }));
      tonesManagement.resetEditState();
      toast.success("Peso editado");
    } catch (error: unknown) {
      tonesManagement.setIsUpdateNewTon(false);
      toast.error(getErrorMessage(error));
      options?.onError?.();
    } finally {
      options?.onFinally?.();
    }
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, []);

  return (
    <ExpeditionQueueStatusBase
      list={{
        data: data[activeArray],
        Wrapper: (children) => <TonsContext.Provider value={{ handleUpdateTons }}>{children}</TonsContext.Provider>,
        render: (vehicle) => <AttendanceItem attendance={vehicle} key={vehicle.id} />
      }}
      loading={state.isLoading}
      slots={{
        search: {
          props: {
            searchValue: search.value,
            onSearchChange: search.onChange
          }
        }
      }}
    />
  );
}

interface AttendanceItemProps {
  attendance: VehiclesAttendedData;
}
const AttendanceItem = memo(({ attendance }: AttendanceItemProps) => {
  const router = useRouter();

  const [qtyTransported, setQtyTransported] = useState(attendance?.scheduling?.qtyTransported?.toString() || "");
  const [loading, { open: setLoading, close: unsetLoading }] = useDisclosure(false);
  const [edit, { close, toggle }] = useDisclosure(false);

  const { expedition } = useSession();
  const { refetch } = useRefetch();
  const { handleUpdateTons } = useTons();

  const today = dayjs().toString();
  const compareAttendendedAt = useMemo(() => dayjs(attendance.attended_at).isSame(today, "day"), [attendance.attended_at, today]);
  const role = expedition?.role;
  const isExpeditionViewer = role === Role.Espectador;
  const isConciergeViewer = role === Role.Portaria;

  const handleSave = async () => {
    const handleDismiss = () => {
      close();
      unsetLoading();
      setQtyTransported(attendance.scheduling.qtyTransported.toString());
    };

    handleUpdateTons(
      {
        driverId: attendance.id,
        schedulingId: attendance.scheduling.id,
        newQtyTransported: qtyTransported
      },
      {
        onInit: () => {
          setLoading();
        },
        onError: () => {
          handleDismiss();
        },
        onSuccess: () => {
          refetch("serviceStatus");
          router.invalidate();
        },
        onFinally: () => {
          handleDismiss();
        }
      }
    );
  };

  return (
    <ExpeditionQueueListItemBase
      driverName={attendance?.driver?.name}
      slots={{
        Plate: { plate: attendance.truck.licensePlate },
        ExpeditionDate: { date: attendance.attended_at }
      }}
      components={{
        Actions: (
          <>
            {!isConciergeViewer && !isExpeditionViewer && compareAttendendedAt && (
              <ExpeditionActionsBase state={{ active: edit, loading }} actions={{ cancel: close, toggleActive: toggle, save: handleSave }} />
            )}
          </>
        ),
        DriverInfo: (
          <>
            {!edit && <p>{attendance.scheduling.qtyTransported}t</p>}
            {edit && (
              <Input.Uncontrolled.Text
                value={qtyTransported}
                onChange={(e) => setQtyTransported(handleMaskQtyTransported(e).qtyTransported)}
                rightSection="t"
                size="sm"
                radius="sm"
              />
            )}
          </>
        )
      }}
    />
  );
});
AttendanceItem.displayName = "AttendanceItem";

// Hook customizado para gerenciar estado dos tons
function useTonesManagement() {
  const [newQtyTransported, setNewQtyTransported] = useState("");
  const [isEditTons, setEditTons] = useState(false);
  const [isUpdateNewTon, setIsUpdateNewTon] = useState(false);
  const [whichDriverIsEdit, setWhichDriverIsEdit] = useState<number | null>(null);

  const newQtyTransportedIsZero = Number(newQtyTransported.replace(/,/g, "")) === 0;

  const updateQtyTransported = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { qtyTransported } = handleMaskQtyTransported(event);
    setNewQtyTransported(qtyTransported);
  };

  const handleEnableTonsEditing = (driverId: number) => {
    setEditTons(true);
    setWhichDriverIsEdit(driverId);
  };

  const handleDisableTonsEditing = () => {
    setEditTons(false);
  };

  const resetEditState = () => {
    setNewQtyTransported("");
    setIsUpdateNewTon(false);
    handleDisableTonsEditing();
  };

  return {
    newQtyTransported,
    isEditTons,
    isUpdateNewTon,
    whichDriverIsEdit,
    newQtyTransportedIsZero,
    updateQtyTransported,
    handleEnableTonsEditing,
    handleDisableTonsEditing,
    setIsUpdateNewTon,
    resetEditState
  };
}

// Utilitário para tratar erros de API
function getErrorMessage(error: unknown): string {
  const unknownError = "Erro desconhecido";

  if (error instanceof AxiosError) {
    const response = error.response as { data?: { message?: string } };
    return response.data?.message || unknownError;
  }
  return unknownError;
}

// Hook customizado para gerenciar veículos
function useVehiclesAttendance() {
  const { expedition } = useSession();
  const companyId = expedition?.company.id;

  const [vehiclesAttended, setVehiclesAttended] = useState<NextApp.DataState<VehiclesAttendedData[]>>({
    data: [],
    isLoading: false,
    isError: false
  });
  const [vehiclesAttendedFiltered, setVehiclesAttendedFiltered] = useState<VehiclesAttendedData[]>([]);

  const [searchByLicensePlate, setSearchByLicensePlate] = useState("");

  const sortByAttendedAt = (queueA: VehiclesAttendedData, queueB: VehiclesAttendedData) => {
    if (queueA.attended_at < queueB.attended_at) return 1;
    if (queueA.attended_at > queueB.attended_at) return -1;
    return 0;
  };

  const getVehiclesAttendance = async () => {
    setVehiclesAttended((prev) => ({ ...prev, isLoading: true }));
    try {
      const { data } = await api.get(`/queue/attendeds/${companyId}`);
      const sortedData = data.sort(sortByAttendedAt);
      setVehiclesAttended((prev) => ({ ...prev, data: sortedData }));
    } catch (error) {
      console.error("Erro ao buscar veículos atendidos:", error);
      setVehiclesAttended((prev) => ({ ...prev, isError: true }));
    } finally {
      setVehiclesAttended((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const getVehicleByLicensePlate = async () => {
    if (searchByLicensePlate.length > 0) {
      try {
        const formattedLicensePlate = searchByLicensePlate.toUpperCase();
        const { data } = await api.get(`/queue/attendeds/${companyId}?licensePlate=${formattedLicensePlate}`);
        setVehiclesAttendedFiltered(data);
      } catch (error) {
        console.error("Erro ao buscar veículo por placa:", error);
      }
    }
  };

  return {
    vehiclesAttended,
    vehiclesAttendedFiltered,
    searchByLicensePlate,
    setSearchByLicensePlate,
    setVehiclesAttended,
    getVehiclesAttendance,
    getVehicleByLicensePlate
  };
}
